/**
 * Inline SVG stacked columns of retailMinor by tenant (TMMPDM-0020): one chart per currency —
 * a tenant appears only in its own currency's chart (never stacked across currencies).
 */
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, SectionCard } from '@/components/page';
import { formatMoneyWhole } from '@/format';
import type { TenantDashboard } from '@/api/tmm/types';
import type { Period } from './period';
import type { FleetData } from './useFleetDashboard';

const OPACITIES = [1, 0.65, 0.4, 0.25, 0.15];
const HEIGHT = 160;

function ChartSurface({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <SectionCard title={title} headingLevel="h3">
        {children}
      </SectionCard>
    </Box>
  );
}

interface RetailByTenantChartProps {
  data: FleetData | null;
  loading: boolean;
  period: Period;
  locale: string;
}

function CurrencyChart({
  currency,
  tenants,
  period,
  locale,
}: {
  currency: string;
  tenants: TenantDashboard[];
  period: Period;
  locale: string;
}) {
  const { t } = useCopy();
  const theme = useTheme();
  const dates = [
    ...new Set(tenants.flatMap((tenant) => tenant.series.map((point) => point.date))),
  ].sort();

  if (dates.length === 0) {
    return (
      <ChartSurface
        title={t('dashboard.chart.retailTitleCurrency', 'Retail by tenant · {{currency}}', {
          currency,
        })}
      >
        <EmptyState title={t('dashboard.chart.empty', 'No activity in this period.')} />
      </ChartSurface>
    );
  }

  const columns = dates.map((date) => {
    const slices = tenants.map(
      (tenant) => tenant.series.find((point) => point.date === date)?.retailMinor ?? 0,
    );
    // Display scaling only — column total is geometry, not a truth claim.
    const total = slices.reduce((sum, value) => sum + value, 0);
    return { date, slices, total };
  });
  const maxTotal = Math.max(...columns.map((column) => column.total), 1);
  const gap = 8;
  const colWidth = Math.max(12, (100 - gap * (columns.length + 1)) / columns.length);

  return (
    <ChartSurface
      title={t('dashboard.chart.retailTitleCurrency', 'Retail by tenant · {{currency}}', {
        currency,
      })}
    >
      <svg
        role="img"
        width="100%"
        height={HEIGHT}
        viewBox={`0 0 100 ${HEIGHT}`}
        preserveAspectRatio="none"
        aria-label={t(
          'dashboard.chart.retailAriaCurrency',
          'Retail by tenant in {{currency}}, {{from}} to {{to}}',
          { currency, from: period.from, to: period.to },
        )}
      >
        {columns.map((column, index) => {
          const x = gap + index * (colWidth + gap);
          let y = HEIGHT;
          return (
            <g key={column.date}>
              <title>
                {`${column.date} · ${formatMoneyWhole(column.total, currency, locale)}`}
              </title>
              {column.slices.map((minor, tenantIndex) => {
                const h = (minor / maxTotal) * (HEIGHT - 8);
                y -= h;
                const opacity = OPACITIES[tenantIndex % OPACITIES.length] ?? 0.15; // opacity-exempt: series tint
                return (
                  <rect
                    key={`${column.date}-${tenantIndex}`}
                    x={x}
                    y={y}
                    width={colWidth}
                    height={h}
                    style={{ fill: (theme.vars ?? theme).palette.primary.main }}
                    fillOpacity={opacity} // opacity-exempt: series tint
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
        {tenants.map((tenant, index) => (
          <Typography
            key={tenant.slug}
            variant="caption"
            color="text.secondary"
            sx={(theme) => theme.typography.mono}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 8,
                height: 8,
                mr: 0.5,
                bgcolor: 'primary.main',
                opacity: OPACITIES[index % OPACITIES.length] ?? 0.15, // opacity-exempt: series tint
              }}
            />
            {tenant.slug}
          </Typography>
        ))}
      </Stack>
    </ChartSurface>
  );
}

export function RetailByTenantChart({ data, loading, period, locale }: RetailByTenantChartProps) {
  const { t } = useCopy();

  if (!data) {
    if (!loading) return null;
    return (
      <ChartSurface title={t('dashboard.chart.retailTitle', 'Retail by tenant')}>
        <Typography variant="body2" color="text.secondary">
          {t('common.loading', 'Loading')}
        </Typography>
      </ChartSurface>
    );
  }

  const byCurrency = new Map<string, TenantDashboard[]>();
  for (const tenant of data.dashboard.tenants) {
    const slug = tenant.slug;
    if (!slug) continue;
    const currency = data.tenantsBySlug.get(slug)?.currency;
    if (!currency) continue;
    const list = byCurrency.get(currency) ?? [];
    list.push(tenant);
    byCurrency.set(currency, list);
  }

  if (byCurrency.size === 0) {
    return (
      <ChartSurface title={t('dashboard.chart.retailTitle', 'Retail by tenant')}>
        <EmptyState title={t('dashboard.chart.empty', 'No activity in this period.')} />
      </ChartSurface>
    );
  }

  return (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
      {[...byCurrency.entries()].map(([currency, tenants]) => (
        <CurrencyChart
          key={currency}
          currency={currency}
          tenants={tenants}
          period={period}
          locale={locale}
        />
      ))}
    </Stack>
  );
}
